import React, {useState, useMemo, useCallback, useEffect, useRef} from "react";
import {
    Search, Spinner, Button, ButtonIcon, Text, Stack,
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
    Tooltip, TooltipTrigger, TooltipContent
} from "@nike/podium";
import {Check, Tip, AlertCircle} from "@nike/nike-design-system-icons";
import {ImbalanceItem, BucketConfig, BatchProgress, ActionedRecord} from "../../common/data/types";
import {adjUnits, skuKey} from "../../common/data/imbalanceHelpers";
import {ConfirmActionDialog} from "./ConfirmActionDialog";

type BucketProps = {
    config: BucketConfig;
    rows: ImbalanceItem[];
    filter: string;
    onFilterChange: (val: string) => void;
    onPerformAction: (selectedItems: ImbalanceItem[]) => Promise<{failedItems: number}>;
    onClearProgress: () => void;
    isDone: boolean;
    startOpen: boolean;
    progress: BatchProgress | null;
    actionedSkus: Record<string, ActionedRecord>;
};

type ActionState = 'idle' | 'loading';

export function Bucket({
    config, rows, filter, onFilterChange,
    onPerformAction, onClearProgress, isDone, startOpen, progress, actionedSkus
}: BucketProps) {
    const [isOpen, setIsOpen] = useState(startOpen);
    const [actionState, setActionState] = useState<ActionState>('idle');
    const [statusText, setStatusText] = useState('');
    const [hasError, setHasError] = useState(false);
    const [deselected, setDeselected] = useState<Set<number>>(new Set());
    const [showDialog, setShowDialog] = useState(false);
    const lastClickedRef = useRef<number | null>(null);

    useEffect(() => {
        setIsOpen(startOpen);
        setActionState('idle');
        setStatusText('');
        setHasError(false);
        setDeselected(new Set());
        lastClickedRef.current = null;
    }, [rows, startOpen]);

    const totalUnits = useMemo(() => rows.reduce((s, item) => s + adjUnits(item), 0), [rows]);

    const visible = useMemo(() => {
        const q = filter.toLowerCase();
        return rows.map((item, i) => ({item, i})).filter(({item}) => !q || item.material.toLowerCase().includes(q));
    }, [rows, filter]);

    const allVisibleSelected = visible.length > 0 && visible.every(({i}) => !deselected.has(i));
    const someVisibleSelected = visible.some(({i}) => !deselected.has(i));

    const toggleSelectAll = useCallback(() => {
        setDeselected(prev => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                visible.forEach(({i}) => next.add(i));
            } else {
                visible.forEach(({i}) => next.delete(i));
            }
            return next;
        });
    }, [visible, allVisibleSelected]);

    const toggleRow = useCallback((origIdx: number, shiftKey: boolean) => {
        setDeselected(prev => {
            const next = new Set(prev);

            if (shiftKey && lastClickedRef.current !== null) {
                const visibleIndices = visible.map(({i}) => i);
                const lastPos = visibleIndices.indexOf(lastClickedRef.current);
                const currentPos = visibleIndices.indexOf(origIdx);

                if (lastPos !== -1 && currentPos !== -1) {
                    const start = Math.min(lastPos, currentPos);
                    const end = Math.max(lastPos, currentPos);
                    const shouldSelect = next.has(origIdx);

                    for (let pos = start; pos <= end; pos++) {
                        if (shouldSelect) {
                            next.delete(visibleIndices[pos]);
                        } else {
                            next.add(visibleIndices[pos]);
                        }
                    }
                } else {
                    if (next.has(origIdx)) next.delete(origIdx);
                    else next.add(origIdx);
                }
            } else {
                if (next.has(origIdx)) {
                    next.delete(origIdx);
                } else {
                    next.add(origIdx);
                }
            }

            return next;
        });
        lastClickedRef.current = origIdx;
    }, [visible]);

    const selectedItems = useMemo(
        () => rows.filter((_, i) => {
            if (deselected.has(i)) return false;
            const actioned = actionedSkus[skuKey(rows[i])];
            return !actioned || actioned.status !== 'processed';
        }),
        [rows, deselected, actionedSkus],
    );

    const selectedUnits = useMemo(
        () => selectedItems.reduce((s, item) => s + adjUnits(item), 0),
        [selectedItems],
    );

    const handleConfirm = useCallback(async () => {
        if (actionState !== 'idle' || selectedItems.length === 0) return;
        setActionState('loading');

        try {
            const {failedItems: failedCount} = await onPerformAction(selectedItems);
            if (failedCount > 0) {
                const succeeded = selectedItems.length - failedCount;
                setStatusText(`${succeeded} of ${selectedItems.length} items adjusted · ${failedCount} failed`);
                setHasError(true);
            } else {
                setStatusText(`${selectedItems.length} items · ${selectedUnits} units adjusted`);
                setHasError(false);
            }
            setActionState('idle');
        } catch {
            setActionState('idle');
            setStatusText('');
            setHasError(false);
        }
    }, [actionState, selectedItems, selectedUnits, onPerformAction]);

    const handleCloseDialog = useCallback(() => {
        setShowDialog(false);
        onClearProgress();
    }, [onClearProgress]);

    const renderActionButton = () => {
        if (actionState === 'loading') {
            return (
                <Button size="s" variant="outlined" disabled className="im-action-loading">
                    <ButtonIcon>
                        <Spinner size="small" />
                    </ButtonIcon>
                    Processing…
                </Button>
            );
        }
        if (isDone) {
            return (
                <Button size="s" variant="outlined" disabled className="im-action-success">
                    <ButtonIcon>
                        <Check size="s" />
                    </ButtonIcon>
                    Done
                </Button>
            );
        }
        return (
            <Button
                size="s"
                disabled={selectedItems.length === 0}
                onClick={() => setShowDialog(true)}
            >
                <ButtonIcon>
                    <Tip size="s" />
                </ButtonIcon>
                {config.btnLabel}
            </Button>
        );
    };

    const accordionValue = isOpen ? [config.key] : [];

    return (
        <Accordion
            type="multiple"
            value={accordionValue}
            onValueChange={(vals) => {
                if (!isDone) setIsOpen(vals.includes(config.key));
            }}
            className={`im-bucket ${isDone ? 'is-done' : ''}`}
        >
            <AccordionItem value={config.key}>
                <AccordionTrigger>
                    <div className="im-bucket-trigger-content">
                        <span className="im-bucket-name">{config.name}</span>
                        <div className="im-bucket-meta">
                            <strong>{isDone ? rows.length : selectedItems.length}</strong>
                            <span>{isDone ? 'records adjusted' : `of ${rows.length} records selected`}</span>
                            <span className="sep">&middot;</span>
                            <strong>{isDone ? totalUnits : selectedUnits}</strong>
                            <span style={isDone ? {color: 'var(--pds-color-text-success)'} : undefined}>
                                {isDone ? 'units adjusted' : `of ${totalUnits} units`}
                            </span>
                            <span className="sep">&middot;</span>
                            <span className={`im-badge im-${config.adjBadgeClass}`}>{config.adjBadgeText}</span>
                            <span className={`im-badge im-${config.lcBadgeClass}`}>
                                {config.lcDotColor && (
                                    <span className="im-lc-dot" style={{background: config.lcDotColor}} />
                                )}
                                {config.lcBadgeText}
                            </span>
                            <span className="im-bucket-action-lbl">{config.actionLabel}</span>
                            <span className="im-bkt-done-check">
                                <Check size="s" />
                                Done
                            </span>
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    <Stack horizontal align="center" gap="s" className="im-bucket-filter-bar">
                        <Search
                            id={`bucket-search-${config.key}`}
                            label={`Filter ${config.name} by material code`}
                            placeholder="Filter by material code…"
                            value={filter}
                            onValueChange={onFilterChange}
                        />
                        {filter && (
                            <Text font="body-4" as="span" color="secondary" className="im-bkt-match-count">
                                <strong>{visible.length}</strong> of {rows.length} shown
                            </Text>
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="im-limit-notice">
                                    <AlertCircle size="s" />
                                    Top 1,000 by persistent imbalance size
                                </span>
                            </TooltipTrigger>
                            <TooltipContent size="s">
                                For performance reasons, only the top 1,000 persistent imbalances per bucket are loaded
                            </TooltipContent>
                        </Tooltip>
                    </Stack>

                    {rows.length === 0 ? (
                        <Stack align="center" padding={20}>
                            <Text font="body-4" color="secondary">No records in this bucket.</Text>
                        </Stack>
                    ) : visible.length === 0 ? (
                        <Stack align="center" padding={20}>
                            <Text font="body-4" color="secondary">
                                No materials match "<strong>{filter}</strong>".
                            </Text>
                        </Stack>
                    ) : (
                        <div className="im-bc-table-wrap">
                            <table className="im-bc-table">
                                <thead>
                                    <tr>
                                        <th className="td-sel">
                                            <input
                                                type="checkbox"
                                                checked={allVisibleSelected}
                                                ref={el => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                                                onChange={toggleSelectAll}
                                                aria-label="Select all"
                                            />
                                        </th>
                                        <th>Material</th>
                                        <th>Size</th>
                                        <th>Quality</th>
                                        <th>COO</th>
                                        <th>WMS Qty</th>
                                        <th>CI Bonded Qty</th>
                                        <th>CI Non-bonded Qty</th>
                                        <th>CI Total Qty</th>
                                        <th>Latest imbalance</th>
                                        <th>Persistent imbalance units to adjust</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map(({item, i}) => {
                                        const actioned = actionedSkus[skuKey(item)];
                                        const isProcessed = actioned?.status === 'processed';
                                        return (
                                            <BucketRow
                                                key={`${item.material}-${i}`}
                                                item={item}
                                                selected={!deselected.has(i)}
                                                onToggle={(shiftKey) => toggleRow(i, shiftKey)}
                                                isProcessed={isProcessed}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <Stack horizontal align="center" gap="s" className="im-bucket-foot">
                        {renderActionButton()}
                        {statusText && (
                            <Text font="body-4" as="span" className={`im-status-chip ${hasError ? 'error' : 'done'}`}>
                                {statusText}
                            </Text>
                        )}
                    </Stack>
                </AccordionContent>
            </AccordionItem>

            {showDialog && (
                <ConfirmActionDialog
                    bucketKey={config.key}
                    recordCount={selectedItems.length}
                    totalUnits={selectedUnits}
                    progress={progress}
                    onConfirm={handleConfirm}
                    onClose={handleCloseDialog}
                />
            )}
        </Accordion>
    );
}

type BucketRowProps = {
    item: ImbalanceItem;
    selected: boolean;
    onToggle: (shiftKey: boolean) => void;
    isProcessed: boolean;
};

function BucketRow({item, selected, onToggle, isProcessed}: BucketRowProps) {
    const {material, size, quality, coo, wmsQty, ciBondedQty, ciNonBondedQty, ciTotalQty, imbalance, persistentImbalance} = item;

    const renderImbalance = () => {
        if (wmsQty === 0) return <span className="im-iv im-iv-zero">{imbalance}</span>;
        if (imbalance < 0) return <span className="im-iv im-iv-neg">{imbalance}</span>;
        return <span className="im-iv im-iv-pos">+{imbalance}</span>;
    };

    return (
        <tr className={isProcessed ? 'is-processed' : ''}>
            <td className="td-sel">
                <input
                    type="checkbox"
                    checked={selected}
                    onClick={(e) => onToggle(e.shiftKey)}
                    onChange={() => {}}
                    disabled={isProcessed}
                    aria-label={`Select ${material}`}
                />
            </td>
            <td><strong>{material}</strong></td>
            <td>{size}</td>
            <td>{quality}</td>
            <td>{coo}</td>
            <td>{wmsQty === 0 ? <span style={{fontWeight: 600}}>0</span> : wmsQty}</td>
            <td>{ciBondedQty > 0 ? ciBondedQty : <span style={{color: 'var(--pds-color-text-secondary)'}}>0</span>}</td>
            <td>{ciNonBondedQty > 0 ? ciNonBondedQty : <span style={{color: 'var(--pds-color-text-secondary)'}}>0</span>}</td>
            <td><strong>{ciTotalQty}</strong></td>
            <td>{renderImbalance()}</td>
            <td style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
                {persistentImbalance > 0 ? '+' : ''}{persistentImbalance}
            </td>
        </tr>
    );
}
